// generated with @7nohe/openapi-react-query-codegen@1.6.2 

import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { DefaultService } from "../requests/services.gen";
import { CandleField, CandleFilter, CandleOption, HiroStreamString, IntradayBarType, IntradayGreek, MarketActorKind, MoviefoneChatBody, OptionAccumulationType, OptionOrigin, Order, StructureRequest } from "../requests/types.gen";
import * as Common from "./common";
/**
* The handler for the auth request which generates a token to validate based on basic client
* ID/secret authentication
* @param data The data for the request.
* @param data.authorization Basic Authentication
* @returns string Success
* @throws ApiError
*/
export const useDefaultServiceGetAuth = <TData = Common.DefaultServiceGetAuthDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ authorization }: {
  authorization: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetAuthKeyFn({ authorization }, queryKey), queryFn: () => DefaultService.getAuth({ authorization }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.sym
* @param data.stream
* @param data.field
* @param data.option
* @param data.filter
* @param data.limit
* @param data.start start timestamp in milliseconds
* @param data.end end timestamp in milliseconds
* @returns string Success
* @throws ApiError
*/
export const useDefaultServiceGetCandles = <TData = Common.DefaultServiceGetCandlesDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ end, field, filter, limit, option, start, stream, sym }: {
  end?: number;
  field?: CandleField;
  filter?: CandleFilter;
  limit?: number;
  option?: CandleOption;
  start?: number;
  stream?: HiroStreamString;
  sym: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetCandlesKeyFn({ end, field, filter, limit, option, start, stream, sym }, queryKey), queryFn: () => DefaultService.getCandles({ end, field, filter, limit, option, start, stream, sym }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.symbol
* @param data.optionAccumulationType
* @param data.optionOrigin
* @param data.isFiltered
* @param data.candleDurationMs duration of candles. Must be a multiple of 5000
* @param data.timeGte ISO 8601 formatted date-time string like "2025-01-01T05:01:17"
* @param data.timeLt ISO 8601 formatted date-time string like "2025-01-01T05:01:17"
* @param data.limit max number of items to return. Default 100, max 20_000
* @returns DeltaSignalRsp Success
* @throws ApiError
*/
export const useDefaultServiceGetDeltaSignal = <TData = Common.DefaultServiceGetDeltaSignalDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ candleDurationMs, isFiltered, limit, optionAccumulationType, optionOrigin, symbol, timeGte, timeLt }: {
  candleDurationMs: number;
  isFiltered: boolean;
  limit?: number;
  optionAccumulationType: OptionAccumulationType;
  optionOrigin: OptionOrigin;
  symbol: string;
  timeGte?: string;
  timeLt?: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetDeltaSignalKeyFn({ candleDurationMs, isFiltered, limit, optionAccumulationType, optionOrigin, symbol, timeGte, timeLt }, queryKey), queryFn: () => DefaultService.getDeltaSignal({ candleDurationMs, isFiltered, limit, optionAccumulationType, optionOrigin, symbol, timeGte, timeLt }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.symbol
* @param data.candleDurationMs duration of candles. Must be a multiple of 5000
* @param data.timeGte ISO 8601 formatted date-time string like "2025-01-01T05:01:17"
* @param data.timeLt ISO 8601 formatted date-time string like "2025-01-01T05:01:17"
* @param data.limit max number of items to return. Default 100, max 20_000
* @param data.order order the candles are fetched and returned in. Default "asc"
* @returns CandlesRsp Success
* @throws ApiError
*/
export const useDefaultServiceGetPrices = <TData = Common.DefaultServiceGetPricesDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ candleDurationMs, limit, order, symbol, timeGte, timeLt }: {
  candleDurationMs: number;
  limit?: number;
  order?: Order;
  symbol: string;
  timeGte?: string;
  timeLt?: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetPricesKeyFn({ candleDurationMs, limit, order, symbol, timeGte, timeLt }, queryKey), queryFn: () => DefaultService.getPrices({ candleDurationMs, limit, order, symbol, timeGte, timeLt }) as TData, ...options });
/**
* Fetches the list of available instruments for the day
* @returns Manifest
* @throws ApiError
*/
export const useDefaultServiceGetSgManifest = <TData = Common.DefaultServiceGetSgManifestDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>(queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetSgManifestKeyFn(queryKey), queryFn: () => DefaultService.getSgManifest() as TData, ...options });
/**
* @param data The data for the request.
* @param data.filters
* @param data.sorting
* @param data.offset
* @param data.limit
* @returns ContractRow Success
* @throws ApiError
*/
export const useDefaultServiceGetSgTnsContracts = <TData = Common.DefaultServiceGetSgTnsContractsDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ filters, limit, offset, sorting }: {
  filters?: string;
  limit?: number;
  offset?: number;
  sorting?: string;
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetSgTnsContractsKeyFn({ filters, limit, offset, sorting }, queryKey), queryFn: () => DefaultService.getSgTnsContracts({ filters, limit, offset, sorting }) as TData, ...options });
/**
* The handler for the Time & Sales feed
* @param data The data for the request.
* @param data.filters
* @param data.sorting
* @param data.offset
* @param data.limit
* @returns string Success
* @throws ApiError
*/
export const useDefaultServiceGetSgTnsFeed = <TData = Common.DefaultServiceGetSgTnsFeedDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ filters, limit, offset, sorting }: {
  filters?: string;
  limit?: number;
  offset?: number;
  sorting?: string;
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetSgTnsFeedKeyFn({ filters, limit, offset, sorting }, queryKey), queryFn: () => DefaultService.getSgTnsFeed({ filters, limit, offset, sorting }) as TData, ...options });
/**
* The handler for the Time & Sales flow summary. Aggregate: volume, premium, delta, gamma, vega
* @param data The data for the request.
* @param data.filters
* @returns HiroAggSummary Success
* @throws ApiError
*/
export const useDefaultServiceGetSgTnsFlowSum = <TData = Common.DefaultServiceGetSgTnsFlowSumDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ filters }: {
  filters?: string;
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetSgTnsFlowSumKeyFn({ filters }, queryKey), queryFn: () => DefaultService.getSgTnsFlowSum({ filters }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.start
* @param data.end
* @returns Highlights Success
* @throws ApiError
*/
export const useDefaultServiceGetSgTnsHighlights = <TData = Common.DefaultServiceGetSgTnsHighlightsDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ end, start }: {
  end?: number;
  start?: number;
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetSgTnsHighlightsKeyFn({ end, start }, queryKey), queryFn: () => DefaultService.getSgTnsHighlights({ end, start }) as TData, ...options });
/**
* Health and build information for the server instance handling the request.
* Always reports the deployed commit (`repo_git_hash`). Sending a bearer token additionally
* reports whether it decoded (`able_to_decode_jwt`), and a token carrying the `administrator`
* role also reports the instance hostname plus a live reachability probe of each backing
* service (Redis cluster, users/hiro/crunchy Postgres, Scylla, S3) - any probe that fails turns
* the response into a 500 while still returning the per-service detail.
* @param data The data for the request.
* @param data.authorization Bearer <jwt-token>
* @returns ServerStatus Success
* @throws ApiError
*/
export const useDefaultServiceGetStatus = <TData = Common.DefaultServiceGetStatusDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ authorization }: {
  authorization: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetStatusKeyFn({ authorization }, queryKey), queryFn: () => DefaultService.getStatus({ authorization }) as TData, ...options });
/**
* The handler for the HTTP request (this gets called when the HTTP GET lands at the start
* of websocket negotiation). After this completes, the actual switching from HTTP to
* websocket protocol will occur.
* This is the last point where we can extract TCP/IP metadata such as IP address of the client
* as well as things from HTTP headers such as user-agent of the browser etc.
* @param data The data for the request.
* @param data.token
* @throws ApiError
*/
export const useDefaultServiceGetStream = <TData = Common.DefaultServiceGetStreamDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ token }: {
  token: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetStreamKeyFn({ token }, queryKey), queryFn: () => DefaultService.getStream({ token }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.sym Symbol to query (e.g., NVDA)
* @returns CorrelationRegimeResponse Success
* @throws ApiError
*/
export const useDefaultServiceGetV1CorrelationRegime = <TData = Common.DefaultServiceGetV1CorrelationRegimeDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ sym }: {
  sym: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1CorrelationRegimeKeyFn({ sym }, queryKey), queryFn: () => DefaultService.getV1CorrelationRegime({ sym }) as TData, ...options });
/**
* SpotGamma equity levels and vol metrics by symbol.
* For each requested symbol, the daily Equity Hub row for the trade date: key levels (call and
* put walls, key gamma/delta strikes, hedge wall), gamma and delta exposures, option volumes,
* IV/skew ranks, and per-strike gamma lists - see `EquityLevels` for the full field list.
* Symbols with no data are omitted from the response. Index-combo aliases (e.g. `S&P 500`,
* `Nasdaq`) resolve to their index's levels; `S&P ES=F` additionally shifts price-level fields
* by the index-to-futures offset.
* @param data The data for the request.
* @param data.syms Comma-separated list of symbols, e.g. `SPX,AAPL`
* @param data.date Trade date. If unspecified, defaults to today; either way the most recent payload within
* the prior 5 business days is served.
* @returns EquityLevels Map of requested symbol to its equity-levels row
* @throws ApiError
*/
export const useDefaultServiceGetV1EquitiesBySyms = <TData = Common.DefaultServiceGetV1EquitiesBySymsDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date, syms }: {
  date?: string;
  syms: string[];
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1EquitiesBySymsKeyFn({ date, syms }, queryKey), queryFn: () => DefaultService.getV1EquitiesBySyms({ date, syms }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.sym
* @param data.riskFreeRate
* @param data.holdDays
* @param data.direction
* @param data.futureSpot
* @param data.daysAhead
* @returns MoviefoneAnalysis Runs and retreives analysis of
* @throws ApiError
*/
export const useDefaultServiceGetV1MoviefoneAnalysis = <TData = Common.DefaultServiceGetV1MoviefoneAnalysisDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ daysAhead, direction, futureSpot, holdDays, riskFreeRate, sym }: {
  daysAhead?: number;
  direction: number;
  futureSpot?: number;
  holdDays: number;
  riskFreeRate: number;
  sym: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1MoviefoneAnalysisKeyFn({ daysAhead, direction, futureSpot, holdDays, riskFreeRate, sym }, queryKey), queryFn: () => DefaultService.getV1MoviefoneAnalysis({ daysAhead, direction, futureSpot, holdDays, riskFreeRate, sym }) as TData, ...options });
/**
* @returns MoviefoneConversationSummary The caller's saved conversations, most recently updated first
* @throws ApiError
*/
export const useDefaultServiceGetV1MoviefoneConversations = <TData = Common.DefaultServiceGetV1MoviefoneConversationsDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>(queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1MoviefoneConversationsKeyFn(queryKey), queryFn: () => DefaultService.getV1MoviefoneConversations() as TData, ...options });
/**
* @param data The data for the request.
* @param data.timestamp The conversation's timestamp identifier, as returned by store/list
* @returns unknown The full conversation, brotli-compressed (Content-Encoding: br) so it can be streamed straight through to the browser
* @throws ApiError
*/
export const useDefaultServiceGetV1MoviefoneConversationsByTimestamp = <TData = Common.DefaultServiceGetV1MoviefoneConversationsByTimestampDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ timestamp }: {
  timestamp: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1MoviefoneConversationsByTimestampKeyFn({ timestamp }, queryKey), queryFn: () => DefaultService.getV1MoviefoneConversationsByTimestamp({ timestamp }) as TData, ...options });
/**
* Unusual options positioning (position moves) from the Flow Patrol Opening Setup report.
* Large opening option positions detected for the report date, enriched with premiums,
* open-interest changes, IV/skew ranks, and probability of payoff, ordered by absolute
* premium (largest first). Multileg legs are included and share a multileg_id.
* @param data The data for the request.
* @param data.date Report date. If unspecified, the most recent date with data is served.
* @returns PositionMovesRsp Position moves (unusual flows) for the report date, ordered by absolute premium
* @throws ApiError
*/
export const useDefaultServiceGetV1OpeningSetupPositionMoves = <TData = Common.DefaultServiceGetV1OpeningSetupPositionMovesDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date }: {
  date?: string;
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1OpeningSetupPositionMovesKeyFn({ date }, queryKey), queryFn: () => DefaultService.getV1OpeningSetupPositionMoves({ date }) as TData, ...options });
/**
* Setup Profiles picks from the Flow Patrol Opening Setup report.
* Four vol-surface-screened trade-idea picks for the report date - Bull (flattest put
* skew), Bear (steepest put skew), Short Vol (highest IV/RV), and Long Vol (lowest
* IV/RV) - each a single symbol from the liquid, earnings-clean universe with its
* screening metrics and a human-readable play description.
* @param data The data for the request.
* @param data.date Report date. If unspecified, the most recent date with data is served.
* @returns SetupProfilesRsp Setup Profiles picks for the report date
* @throws ApiError
*/
export const useDefaultServiceGetV1OpeningSetupProfiles = <TData = Common.DefaultServiceGetV1OpeningSetupProfilesDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date }: {
  date?: string;
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1OpeningSetupProfilesKeyFn({ date }, queryKey), queryFn: () => DefaultService.getV1OpeningSetupProfiles({ date }) as TData, ...options });
/**
* Implied-vol scanner from the Flow Patrol Opening Setup report.
* The most active option names for the report date (ranked by option volume) with current
* and prior front-month IV, day-over-day IV change, IV rank/percentile vs trailing
* history, and a practical-read classification of the vol setup.
* @param data The data for the request.
* @param data.date Report date. If unspecified, the most recent date with data is served.
* @returns VolScannerRsp Most-active-options vol scanner for the report date, ordered by option volume
* @throws ApiError
*/
export const useDefaultServiceGetV1OpeningSetupVolScanner = <TData = Common.DefaultServiceGetV1OpeningSetupVolScannerDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date }: {
  date?: string;
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1OpeningSetupVolScannerKeyFn({ date }, queryKey), queryFn: () => DefaultService.getV1OpeningSetupVolScanner({ date }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.symbol
* @returns SettlementStyleResponse Exercise style per expiry date for one underlying
* @throws ApiError
*/
export const useDefaultServiceGetV1SettlementStyle = <TData = Common.DefaultServiceGetV1SettlementStyleDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ symbol }: {
  symbol: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV1SettlementStyleKeyFn({ symbol }, queryKey), queryFn: () => DefaultService.getV1SettlementStyle({ symbol }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.sym If unspecified, defaults to SPX
* @param data.date If unspecified, defaults to SPX
* @param data.mktClose If unspecified, defaults to true
* @returns unknown message-packed expiry strike map
* @throws ApiError
*/
export const useDefaultServiceGetV2DailyGreeks = <TData = Common.DefaultServiceGetV2DailyGreeksDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date, mktClose, sym }: {
  date?: string;
  mktClose?: boolean;
  sym?: string;
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV2DailyGreeksKeyFn({ date, mktClose, sym }, queryKey), queryFn: () => DefaultService.getV2DailyGreeks({ date, mktClose, sym }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.sym
* @returns unknown message-packed expiry strike map
* @throws ApiError
*/
export const useDefaultServiceGetV2LatestGreeks = <TData = Common.DefaultServiceGetV2LatestGreeksDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ sym }: {
  sym: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV2LatestGreeksKeyFn({ sym }, queryKey), queryFn: () => DefaultService.getV2LatestGreeks({ sym }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.symbol
* @param data.mktActor
* @param data.date
* @param data.ts
* @returns unknown Success
* @throws ApiError
*/
export const useDefaultServiceGetV2OpenInterestIntradayDelta = <TData = Common.DefaultServiceGetV2OpenInterestIntradayDeltaDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date, mktActor, symbol, ts }: {
  date?: string;
  mktActor: MarketActorKind;
  symbol: string;
  ts?: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayDeltaKeyFn({ date, mktActor, symbol, ts }, queryKey), queryFn: () => DefaultService.getV2OpenInterestIntradayDelta({ date, mktActor, symbol, ts }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.symbol
* @param data.mktActor
* @param data.date
* @param data.ts
* @returns unknown Success
* @throws ApiError
*/
export const useDefaultServiceGetV2OpenInterestIntradayGamma = <TData = Common.DefaultServiceGetV2OpenInterestIntradayGammaDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date, mktActor, symbol, ts }: {
  date?: string;
  mktActor: MarketActorKind;
  symbol: string;
  ts?: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayGammaKeyFn({ date, mktActor, symbol, ts }, queryKey), queryFn: () => DefaultService.getV2OpenInterestIntradayGamma({ date, mktActor, symbol, ts }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.sym
* @param data.date
* @returns unknown Success
* @throws ApiError
*/
export const useDefaultServiceGetV2OpenInterestIntradayStats = <TData = Common.DefaultServiceGetV2OpenInterestIntradayStatsDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date, sym }: {
  date?: string;
  sym: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayStatsKeyFn({ date, sym }, queryKey), queryFn: () => DefaultService.getV2OpenInterestIntradayStats({ date, sym }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.barType
* @param data.symbol
* @param data.date
* @returns unknown Success
* @throws ApiError
*/
export const useDefaultServiceGetV2OpenInterestIntradayStrikeBars = <TData = Common.DefaultServiceGetV2OpenInterestIntradayStrikeBarsDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ barType, date, symbol }: {
  barType: IntradayBarType;
  date?: string;
  symbol?: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayStrikeBarsKeyFn({ barType, date, symbol }, queryKey), queryFn: () => DefaultService.getV2OpenInterestIntradayStrikeBars({ barType, date, symbol }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.greek
* @param data.mktActor
* @param data.symbol
* @param data.date
* @returns unknown Success
* @throws ApiError
*/
export const useDefaultServiceGetV2OpenInterestIntradayTimestamps = <TData = Common.DefaultServiceGetV2OpenInterestIntradayTimestampsDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ date, greek, mktActor, symbol }: {
  date?: string;
  greek: IntradayGreek;
  mktActor: MarketActorKind;
  symbol?: string;
}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayTimestampsKeyFn({ date, greek, mktActor, symbol }, queryKey), queryFn: () => DefaultService.getV2OpenInterestIntradayTimestamps({ date, greek, mktActor, symbol }) as TData, ...options });
/**
* A simple handler for validating and inspecting a JWT token
* @returns Claims Success
* @throws ApiError
*/
export const useDefaultServiceGetValidateBearer = <TData = Common.DefaultServiceGetValidateBearerDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>(queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetValidateBearerKeyFn(queryKey), queryFn: () => DefaultService.getValidateBearer() as TData, ...options });
/**
* @param data The data for the request.
* @param data.dates
* @returns Page_VixQuote Success
* @throws ApiError
*/
export const useDefaultServiceGetVixQuote = <TData = Common.DefaultServiceGetVixQuoteDefaultResponse, TError = unknown, TQueryKey extends Array<unknown> = unknown[]>({ dates }: {
  dates?: string[];
} = {}, queryKey?: TQueryKey, options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">) => useQuery<TData, TError>({ queryKey: Common.UseDefaultServiceGetVixQuoteKeyFn({ dates }, queryKey), queryFn: () => DefaultService.getVixQuote({ dates }) as TData, ...options });
/**
* @param data The data for the request.
* @param data.requestBody
* @returns unknown SSE stream of chat completion deltas
* @throws ApiError
*/
export const useDefaultServicePostV1MoviefoneChat = <TData = Common.DefaultServicePostV1MoviefoneChatMutationResult, TError = unknown, TContext = unknown>(options?: Omit<UseMutationOptions<TData, TError, {
  requestBody: MoviefoneChatBody;
}, TContext>, "mutationFn">) => useMutation<TData, TError, {
  requestBody: MoviefoneChatBody;
}, TContext>({ mutationFn: ({ requestBody }) => DefaultService.postV1MoviefoneChat({ requestBody }) as unknown as Promise<TData>, ...options });
/**
* @param data The data for the request.
* @param data.requestBody
* @returns MoviefoneChatFollowupRsp Non-streaming chat completion
* @throws ApiError
*/
export const useDefaultServicePostV1MoviefoneChatFollowup = <TData = Common.DefaultServicePostV1MoviefoneChatFollowupMutationResult, TError = unknown, TContext = unknown>(options?: Omit<UseMutationOptions<TData, TError, {
  requestBody: MoviefoneChatBody;
}, TContext>, "mutationFn">) => useMutation<TData, TError, {
  requestBody: MoviefoneChatBody;
}, TContext>({ mutationFn: ({ requestBody }) => DefaultService.postV1MoviefoneChatFollowup({ requestBody }) as unknown as Promise<TData>, ...options });
/**
* @param data The data for the request.
* @param data.xMoviefoneTitle Base64-encoded (UTF-8 before encoding) title for the conversation, stored as an S3 object tag
* @param data.requestBody Raw JSON conversation payload - streamed straight through to storage, not parsed or validated server-side
* @param data.xMoviefoneTimestamp An existing conversation's timestamp, to overwrite it instead of creating a new one
* @param data.xMoviefoneInteracted Set to "true" when this save was directly triggered by the user (asking something) rather than a response simply finishing - controls whether it moves in the list.
* @returns MoviefoneConversationSummary The stored conversation's identifying metadata
* @throws ApiError
*/
export const useDefaultServicePostV1MoviefoneConversations = <TData = Common.DefaultServicePostV1MoviefoneConversationsMutationResult, TError = unknown, TContext = unknown>(options?: Omit<UseMutationOptions<TData, TError, {
  requestBody: string;
  xMoviefoneInteracted?: string;
  xMoviefoneTimestamp?: string;
  xMoviefoneTitle: string;
}, TContext>, "mutationFn">) => useMutation<TData, TError, {
  requestBody: string;
  xMoviefoneInteracted?: string;
  xMoviefoneTimestamp?: string;
  xMoviefoneTitle: string;
}, TContext>({ mutationFn: ({ requestBody, xMoviefoneInteracted, xMoviefoneTimestamp, xMoviefoneTitle }) => DefaultService.postV1MoviefoneConversations({ requestBody, xMoviefoneInteracted, xMoviefoneTimestamp, xMoviefoneTitle }) as unknown as Promise<TData>, ...options });
/**
* @param data The data for the request.
* @param data.riskFreeRate
* @param data.requestBody
* @returns MoviefoneAnalysis Runs and retreives analysis of
* @throws ApiError
*/
export const useDefaultServicePostV1SpotlineAnalysis = <TData = Common.DefaultServicePostV1SpotlineAnalysisMutationResult, TError = unknown, TContext = unknown>(options?: Omit<UseMutationOptions<TData, TError, {
  requestBody: StructureRequest;
  riskFreeRate: number;
}, TContext>, "mutationFn">) => useMutation<TData, TError, {
  requestBody: StructureRequest;
  riskFreeRate: number;
}, TContext>({ mutationFn: ({ requestBody, riskFreeRate }) => DefaultService.postV1SpotlineAnalysis({ requestBody, riskFreeRate }) as unknown as Promise<TData>, ...options });
/**
* @param data The data for the request.
* @param data.timestamp The conversation's timestamp identifier, as returned by store/list
* @returns void The conversation was deleted
* @throws ApiError
*/
export const useDefaultServiceDeleteV1MoviefoneConversationsByTimestamp = <TData = Common.DefaultServiceDeleteV1MoviefoneConversationsByTimestampMutationResult, TError = unknown, TContext = unknown>(options?: Omit<UseMutationOptions<TData, TError, {
  timestamp: string;
}, TContext>, "mutationFn">) => useMutation<TData, TError, {
  timestamp: string;
}, TContext>({ mutationFn: ({ timestamp }) => DefaultService.deleteV1MoviefoneConversationsByTimestamp({ timestamp }) as unknown as Promise<TData>, ...options });
